import { memo, useState, useEffect, useRef } from 'react';
import { NodeResizer } from '@xyflow/react';
import ContentEditable from 'react-contenteditable';
import { stripHtml } from '../../components/StripHtml';
import ActivityEnergyLayer, {
  getEnergyMeterClass,
} from '../../components/energy-meter/ActivityEnergyLayer';

const PentagonNode = ({ data, id, selected, editable, langCode }) => {
  const [label, setLabel] = useState(stripHtml(data.label || ''));
  const [width, setWidth] = useState(data.width_height?.width || 150);
  const [height, setHeight] = useState(data.width_height?.height || 150);

  const textareaRef = useRef(null);

  const isSelected = selected;
  const nodeId = id || data.id;
  const em = data?.em;
  const emClass = getEnergyMeterClass(em, nodeId);

  // Sync size
  useEffect(() => {
    if (data.width_height?.width && data.width_height?.height) {
      setWidth(data.width_height.width);
      setHeight(data.width_height.height);
    }
  }, [data.width_height]);

  // Sync label (only when not focused to preserve spaces/cursor)
  useEffect(() => {
    const isFocused = textareaRef.current && document.activeElement === textareaRef.current;
    if (!isFocused) {
      setLabel(stripHtml(data.label || ''));
    }
  }, [data.label]);

  // Auto focus in edit mode when selected
  useEffect(() => {
    if (editable && isSelected && textareaRef.current) {
      const timeout = setTimeout(() => {
        textareaRef.current?.focus();

        // Move caret to the end of content
        const el = textareaRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [isSelected, editable]);

  const handleChange = (e) => {
    const newValue = e.target.value || '';
    setLabel(newValue);
    data.onLabelChange?.(newValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleResize = (e, size) => {
    if (!size?.width || !size?.height) return;
    setWidth(size.width);
    setHeight(size.height);
    data.updateWidthHeight?.(id, { width: size.width, height: size.height });
  };

  const handleNodeClick = (e) => {
    if (!editable && em && em.active) {
      e.stopPropagation();
      if (em.mode === 'analysis') {
        em.onOpenPopover({ id: nodeId, title: label });
      }
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        filter: data.hasNextLevel ? 'drop-shadow(0px 0px 10px #0000004f)' : 'none',
      }}
      onClick={handleNodeClick}
    >
      {!editable && <ActivityEnergyLayer data={data} activityId={nodeId} activityTitle={label} />}
      <div
        className={!editable ? `pentagon_box_inner ${emClass}` : ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          backgroundColor: 'red',
          color: '#002060',
          padding: '20px 10px 10px 10px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          border: 'none',
          width,
          height,
          clipPath: 'polygon(50% 0%, 100% 30%, 100% 100%, 0% 100%, 0% 30%)',
        }}
      >
        {editable ? (
          <ContentEditable
            innerRef={textareaRef}
            html={label}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            placeholder=""
            style={styles.textarea}
            className="nodrag rb-process-model-hyphenated-text"
            lang={(langCode || 'de').toLowerCase()}
          />
        ) : (
          <div
            className="pentagon_textbox rb-process-model-hyphenated-text"
            lang={(langCode || 'de').toLowerCase()}
            style={styles.textLabel}
          >
            {label}
          </div>
        )}
      </div>

      {editable && isSelected && (
        <NodeResizer
          minWidth={120}
          minHeight={80}
          onResize={handleResize}
          handleClassName="customHandle"
          lineClassName="customLine"
        />
      )}
    </div>
  );
};

const styles = {
  textarea: {
    background: 'transparent',
    border: 'none',
    color: '#fff',
    caretColor: '#fff',
    fontSize: '12px',
    lineHeight: '1.1',
    width: '100%',
    outline: 'none',
    textAlign: 'center',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    fontFamily: "'Poppins', sans-serif",
    cursor: 'text',
    boxSizing: 'border-box',
    userSelect: 'text',
    WebkitUserSelect: 'text',
    msUserSelect: 'text',
  },
  textLabel: {
    cursor: 'pointer',
    fontSize: '12px',
    lineHeight: '1.1',
    fontFamily: "'Poppins', sans-serif",
    color: 'white',
    textAlign: 'center',
    width: '100%',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    boxSizing: 'border-box',
  },
};

export default memo(PentagonNode);
